import type {
  MotionAmplificationParameters,
  MotionAmplificationPreset,
} from "../../clients/motion-amplification";

type NullableNumericParameter =
  | "frequencyLowHz"
  | "frequencyHighHz"
  | "alpha"
  | "lambdaC"
  | "stabilizeSmooth"
  | "maxWidth"
  | "pyramidLevels"
  | "chromAttenuation"
  | "chunkSize"
  | "overlap"
  | "stabilizeSmoothPasses"
  | "stabilizeMaxFeatures"
  | "stabilizeRansacPx"
  | "stabilizeFastThreshold"
  | "heatmapAlpha";

export type MotionAmplificationFormParameters = Omit<
  MotionAmplificationParameters,
  NullableNumericParameter
> & {
  [Key in NullableNumericParameter]: MotionAmplificationParameters[Key] | null;
};

export const PRESET_DEFAULTS: Record<
  MotionAmplificationPreset,
  Pick<
    MotionAmplificationParameters,
    | "frequencyLowHz"
    | "frequencyHighHz"
    | "alpha"
    | "lambdaC"
    | "stabilize"
    | "stabilizeSmooth"
    | "maxWidth"
  >
> = {
  "standard-60fps": {
    frequencyLowHz: 1,
    frequencyHighHz: 30,
    alpha: 40,
    lambdaC: 16,
    stabilize: true,
    stabilizeSmooth: 21,
    maxWidth: 960,
  },
  "high-frequency-240fps": {
    frequencyLowHz: 10,
    frequencyHighHz: 120,
    alpha: 40,
    lambdaC: 16,
    stabilize: true,
    stabilizeSmooth: 21,
    maxWidth: 960,
  },
  "high-frequency-120fps": {
    frequencyLowHz: 5,
    frequencyHighHz: 60,
    alpha: 20,
    lambdaC: 24,
    stabilize: true,
    stabilizeSmooth: 21,
    maxWidth: 960,
  },
  "high-stability-review": {
    frequencyLowHz: 1,
    frequencyHighHz: 8,
    alpha: 20,
    lambdaC: 24,
    stabilize: true,
    stabilizeSmooth: 41,
    maxWidth: 960,
  },
};

export const ADVANCED_DEFAULTS = {
  pyramidLevels: 6,
  chromAttenuation: 0.2,
  chunkSize: 96,
  overlap: null,
  stabilizeSmoothPasses: 1,
  stabilizeMaxFeatures: 800,
  stabilizeRansacPx: 4,
  stabilizeFastThreshold: 10,
  crop: null,
  heatmap: false,
  sideBySide: false,
  heatmapAlpha: 0.45,
} satisfies Omit<
  MotionAmplificationParameters,
  keyof (typeof PRESET_DEFAULTS)["standard-60fps"]
>;

export const DEFAULT_PRESET: MotionAmplificationPreset = "standard-60fps";
export const MAX_VIDEO_DURATION_SECONDS = 20;
export const MAX_INPUT_BYTES = 500 * 1024 * 1024;
const FPS_MEASUREMENT_TOLERANCE = 0.995;

const MINIMUM_PRESET_FPS: Record<MotionAmplificationPreset, number> = {
  "standard-60fps": 60,
  "high-frequency-240fps": 240,
  "high-frequency-120fps": 120,
  "high-stability-review": 20,
};

export function createDefaultParameters(
  preset: MotionAmplificationPreset = DEFAULT_PRESET,
): MotionAmplificationFormParameters {
  return { ...PRESET_DEFAULTS[preset], ...ADVANCED_DEFAULTS };
}

export function nyquistFrequencyForFps(fps: number | null): number {
  // Keep this aligned with the worker: use the nominal whole-number capture
  // rate before calculating Nyquist, so common 59.94 FPS video accepts 30 Hz.
  return fps === null ? 1000 : Math.min(1000, Math.round(fps) / 2);
}

export function isPresetCompatibleWithFps(
  preset: MotionAmplificationPreset,
  fps: number | null,
): boolean {
  return (
    fps !== null &&
    Number.isFinite(fps) &&
    fps >= MINIMUM_PRESET_FPS[preset] * FPS_MEASUREMENT_TOLERANCE
  );
}

function isFiniteInRange(
  value: number | null,
  min: number,
  max: number,
): value is number {
  return (
    value !== null && Number.isFinite(value) && value >= min && value <= max
  );
}

function isPositiveInteger(value: number | null, max: number): value is number {
  return (
    value !== null && Number.isSafeInteger(value) && value > 0 && value <= max
  );
}

export type MotionAmplificationParameterField =
  | keyof MotionAmplificationFormParameters
  | "frequencyBand"
  | "outputMode"
  | "cropX"
  | "cropY"
  | "cropWidth"
  | "cropHeight";

export type MotionAmplificationParameterValidationError = {
  field: MotionAmplificationParameterField;
  code:
    | "required"
    | "range"
    | "positiveInteger"
    | "frequencyOrder"
    | "nyquist"
    | "chunkOverlap"
    | "cropCoordinate"
    | "cropDimension"
    | "outputConflict";
  min?: number;
  max?: number;
};

export function getMotionAmplificationParameterValidationErrors(
  parameters: MotionAmplificationFormParameters,
  fps: number | null = null,
): MotionAmplificationParameterValidationError[] {
  const errors: MotionAmplificationParameterValidationError[] = [];

  function checkRange(
    field: MotionAmplificationParameterField,
    value: number | null,
    min: number,
    max: number,
  ) {
    if (value === null) {
      errors.push({ field, code: "required", min, max });
    } else if (!isFiniteInRange(value, min, max)) {
      errors.push({ field, code: "range", min, max });
    }
  }

  function checkPositiveInteger(
    field: MotionAmplificationParameterField,
    value: number | null,
    max: number,
  ) {
    if (value === null) {
      errors.push({ field, code: "required", max });
    } else if (!isPositiveInteger(value, max)) {
      errors.push({ field, code: "positiveInteger", max });
    }
  }

  checkRange("frequencyLowHz", parameters.frequencyLowHz, 0.5, 1000);
  checkRange("frequencyHighHz", parameters.frequencyHighHz, 0.5, 1000);
  checkRange("alpha", parameters.alpha, 0, 1000);
  checkRange("lambdaC", parameters.lambdaC, 0, 1000);
  checkPositiveInteger("stabilizeSmooth", parameters.stabilizeSmooth, 1001);

  if (parameters.maxWidth !== null) {
    checkPositiveInteger("maxWidth", parameters.maxWidth, 4096);
  }

  checkPositiveInteger("pyramidLevels", parameters.pyramidLevels, 12);
  checkRange("chromAttenuation", parameters.chromAttenuation, 0, 1);
  checkPositiveInteger("chunkSize", parameters.chunkSize, 2048);

  if (parameters.overlap !== null) {
    if (!isPositiveInteger(parameters.overlap, parameters.chunkSize ?? 0)) {
      errors.push({ field: "overlap", code: "chunkOverlap" });
    } else if (
      parameters.chunkSize !== null &&
      parameters.overlap >= parameters.chunkSize
    ) {
      errors.push({ field: "overlap", code: "chunkOverlap" });
    }
  }

  checkPositiveInteger(
    "stabilizeSmoothPasses",
    parameters.stabilizeSmoothPasses,
    10,
  );
  checkPositiveInteger(
    "stabilizeMaxFeatures",
    parameters.stabilizeMaxFeatures,
    5000,
  );
  checkRange("stabilizeRansacPx", parameters.stabilizeRansacPx, 0.1, 100);
  checkPositiveInteger(
    "stabilizeFastThreshold",
    parameters.stabilizeFastThreshold,
    255,
  );
  checkRange("heatmapAlpha", parameters.heatmapAlpha, 0, 1);

  if (parameters.crop !== null) {
    if (!Number.isSafeInteger(parameters.crop.x) || parameters.crop.x < 0) {
      errors.push({ field: "cropX", code: "cropCoordinate" });
    }
    if (!Number.isSafeInteger(parameters.crop.y) || parameters.crop.y < 0) {
      errors.push({ field: "cropY", code: "cropCoordinate" });
    }
    if (
      !Number.isSafeInteger(parameters.crop.width) ||
      parameters.crop.width <= 0
    ) {
      errors.push({ field: "cropWidth", code: "cropDimension" });
    }
    if (
      !Number.isSafeInteger(parameters.crop.height) ||
      parameters.crop.height <= 0
    ) {
      errors.push({ field: "cropHeight", code: "cropDimension" });
    }
  }

  if (
    parameters.frequencyLowHz !== null &&
    parameters.frequencyHighHz !== null &&
    parameters.frequencyLowHz >= parameters.frequencyHighHz
  ) {
    errors.push({ field: "frequencyBand", code: "frequencyOrder" });
  }

  if (
    parameters.frequencyHighHz !== null &&
    fps !== null &&
    parameters.frequencyHighHz > nyquistFrequencyForFps(fps)
  ) {
    errors.push({
      field: "frequencyHighHz",
      code: "nyquist",
      max: nyquistFrequencyForFps(fps),
    });
  }

  if (parameters.heatmap && parameters.sideBySide) {
    errors.push({ field: "outputMode", code: "outputConflict" });
  }

  return errors;
}

export function isMotionAmplificationParametersValid(
  parameters: MotionAmplificationFormParameters,
  fps: number | null = null,
): parameters is MotionAmplificationParameters {
  return (
    getMotionAmplificationParameterValidationErrors(parameters, fps).length ===
    0
  );
}
